import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { TrendingUp, Printer, Download } from 'lucide-react'
import { useInventoryLedger } from '@/hooks/inventory'
import { Loading } from '@/components/Loading'
import { InlineError } from '@/components/ErrorDisplay'
import { EmptyData } from '@/components/EmptyState'
import { INVENTORY_MOVEMENT_LABELS } from '@/api/types/inventory'

function InventoryLedger() {
  const { id } = useParams()
  const skuCode = id || ''

  const { data: inventoryItem, isLoading, error } = useInventoryLedger(skuCode)

  // Calculate summary from ledger
  const summaryCards = useMemo(() => {
    if (!inventoryItem || !inventoryItem.ledgers) return null

    const ledgers = inventoryItem.ledgers
    const firstLedger = ledgers[0]

    const initialStock = firstLedger?.beforeOnHand || 0
    const totalIn = ledgers.filter((l) => l.deltaOnHand > 0).reduce((sum, l) => sum + l.deltaOnHand, 0)
    const totalOut = Math.abs(ledgers.filter((l) => l.deltaOnHand < 0).reduce((sum, l) => sum + l.deltaOnHand, 0))
    const finalStock = inventoryItem.onHandQty

    return [
      { label: '现有库存', value: String(finalStock), color: 'gray' },
      { label: '本期入库', value: `+${totalIn}`, count: `${ledgers.filter((l) => l.deltaOnHand > 0).length} 笔`, color: 'green' },
      { label: '本期出库', value: `-${totalOut}`, count: `${ledgers.filter((l) => l.deltaOnHand < 0).length} 笔`, color: 'red' },
      { label: '已锁定', value: String(inventoryItem.reservedQty), color: 'amber' },
      { label: '可用库存', value: String(inventoryItem.availableQty), color: 'blue' },
    ]
  }, [inventoryItem])

  const getTypeInfo = (movementType: string) => {
    switch (movementType) {
      case 'PURCHASE_RECEIPT':
        return { label: '采购入库', class: 'bg-green-100 text-green-600', textClass: 'text-green-600', icon: '📥' }
      case 'STOCK_IN':
        return { label: '入库', class: 'bg-green-100 text-green-600', textClass: 'text-green-600', icon: '📥' }
      case 'CONSUME_FOR_REPAIR':
        return { label: '工单消耗', class: 'bg-red-100 text-red-600', textClass: 'text-red-600', icon: '🔧' }
      case 'STOCK_OUT':
        return { label: '出库', class: 'bg-red-100 text-red-600', textClass: 'text-red-600', icon: '📤' }
      case 'RESERVE_FOR_WORK_ORDER':
        return { label: '工单预留', class: 'bg-amber-100 text-amber-600', textClass: 'text-amber-600', icon: '🔒' }
      case 'RELEASE_RESERVATION':
        return { label: '释放预留', class: 'bg-blue-100 text-blue-600', textClass: 'text-blue-600', icon: '🔓' }
      case 'MANUAL_ADJUSTMENT':
        return { label: '手动调整', class: 'bg-purple-100 text-purple-600', textClass: 'text-purple-600', icon: '✏️' }
      default:
        return { label: movementType, class: 'bg-gray-100 text-gray-600', textClass: 'text-gray-600', icon: '📋' }
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <Loading text="加载台账数据..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <InlineError message="加载失败，请重试" />
      </div>
    )
  }

  if (!inventoryItem) {
    return (
      <div className="p-6">
        <EmptyData description="未找到该配件的库存台账信息" />
      </div>
    )
  }

  const ledgers = inventoryItem.ledgers || []

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/supply-chain/inventory/list" className="p-2 hover:bg-gray-100 rounded-lg transition">
              <TrendingUp className="w-5 h-5 text-gray-600 rotate-180" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">库存台账</h1>
              <p className="text-sm text-gray-500 mt-1">
                配件：{inventoryItem.partName} ({skuCode})
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2">
              <Download className="w-4 h-4" />
              导出台账
            </button>
            <button className="px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 transition flex items-center gap-2">
              <Printer className="w-4 h-4" />
              打印
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summaryCards && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          {summaryCards.map((card, index) => {
            const colorClasses = {
              gray: 'text-gray-900',
              green: 'text-green-600',
              red: 'text-red-600',
              amber: 'text-amber-600',
              blue: 'text-blue-600',
            }
            return (
              <div key={index} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="text-xs text-gray-400 mb-1">{card.label}</div>
                <div className={`text-2xl font-bold ${colorClasses[card.color as keyof typeof colorClasses]}`}>
                  {card.value}
                </div>
                <div className="text-xs text-gray-500 mt-1">{card.count || ''}</div>
              </div>
            )
          })}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>规格型号:</span>
            <span className="font-medium text-gray-900">{inventoryItem.specification || '-'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>单位:</span>
            <span className="font-medium text-gray-900">{inventoryItem.unit}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>仓库:</span>
            <span className="font-medium text-gray-900">{inventoryItem.warehouseCode}</span>
          </div>
          <div className="flex-1"></div>
          <div className="text-sm text-gray-500">
            共 <span className="font-bold text-gray-900">{ledgers.length}</span> 条记录
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        {ledgers.length === 0 ? (
          <EmptyData description="该配件暂无台账记录" />
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-sm text-gray-500">
                  <th className="px-5 py-4 font-medium">日期时间</th>
                  <th className="px-5 py-4 font-medium">类型</th>
                  <th className="px-5 py-4 font-medium">单据编号</th>
                  <th className="px-5 py-4 font-medium">来源说明</th>
                  <th className="px-5 py-4 font-medium text-center">入库</th>
                  <th className="px-5 py-4 font-medium text-center">出库</th>
                  <th className="px-5 py-4 font-medium text-center">锁定变化</th>
                  <th className="px-5 py-4 font-medium text-center">结存</th>
                  <th className="px-5 py-4 font-medium">备注</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {ledgers.map((ledger, index) => {
                  const typeInfo = getTypeInfo(ledger.movementType)
                  return (
                    <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-5 py-4 text-gray-600">
                        {new Date(ledger.createdAt).toLocaleString('zh-CN')}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.class} flex items-center gap-1`}>
                          <span>{typeInfo.icon}</span>
                          {INVENTORY_MOVEMENT_LABELS[ledger.movementType] || typeInfo.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-gray-600">{ledger.referenceType}</span>
                        {ledger.referenceId && (
                          <span className="ml-2 text-primary-600 hover:underline cursor-pointer">
                            #{ledger.referenceId}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600">{ledger.remark || '-'}</td>
                      <td className="px-5 py-4 text-center">
                        {ledger.deltaOnHand > 0 ? (
                          <span className={typeInfo.textClass + ' font-medium'}>+{ledger.deltaOnHand}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {ledger.deltaOnHand < 0 ? (
                          <span className={typeInfo.textClass + ' font-medium'}>{Math.abs(ledger.deltaOnHand)}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center">
                        {ledger.deltaReserved !== 0 ? (
                          <span className={ledger.deltaReserved > 0 ? 'text-red-600' : 'text-green-600'} font-medium>
                            {ledger.deltaReserved > 0 ? '+' : ''}
                            {ledger.deltaReserved}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="font-medium text-gray-900">{ledger.afterOnHand}</span>
                        {ledger.afterReserved !== ledger.afterOnHand && (
                          <span className="text-gray-400">({ledger.afterReserved})</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600 text-xs">{ledger.remark || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  )
}

export default InventoryLedger
